// Single API client. The Vite dev server proxies `/api` → backend on :4000;
// in production the URL is read from `VITE_API_URL` (or defaults to same-origin).

import { useAuthStore } from '../store/auth';

const baseUrl: string = import.meta.env.VITE_API_URL || '/api';

export const apiBaseUrl: string = baseUrl;

export interface ApiError {
  status: number;
  code?: string;
  detail?: unknown;
}

// Single-flight refresh: parallel 401s share one /auth/refresh call rather
// than each posting their own.
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(`${baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const body = (await res.json().catch(() => null)) as { accessToken?: string } | null;
      if (!body?.accessToken) return null;
      useAuthStore.getState().setAccessToken(body.accessToken);
      return body.accessToken;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

async function request<T>(
  path: string,
  init: RequestInit & { skipAuth?: boolean; _retried?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('content-type') && init.body && typeof init.body === 'string') {
    headers.set('content-type', 'application/json');
  }

  if (!init.skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers.set('authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
  const isJson = (res.headers.get('content-type') ?? '').includes('application/json');
  const body = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    // Silently refresh on first 401 and retry once. Only clear the session if
    // refresh itself fails (i.e. refresh token expired = 30d truly inactive).
    if (res.status === 401 && !init.skipAuth && !init._retried) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        return request<T>(path, { ...init, _retried: true });
      }
      useAuthStore.getState().clear();
    }
    const err: ApiError = { status: res.status, code: (body as { error?: string })?.error, detail: body };
    console.error('API request failed', {
      path,
      status: res.status,
      body,
    });
    throw err;
  }
  return body as T;
}

export const api = {
  get: <T>(path: string, init: RequestInit = {}): Promise<T> =>
    request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, data?: unknown, init: RequestInit = {}): Promise<T> =>
    request<T>(path, { ...init, method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) }),
  put: <T>(path: string, data?: unknown, init: RequestInit = {}): Promise<T> =>
    request<T>(path, { ...init, method: 'PUT', body: data === undefined ? undefined : JSON.stringify(data) }),
  patch: <T>(path: string, data?: unknown, init: RequestInit = {}): Promise<T> =>
    request<T>(path, { ...init, method: 'PATCH', body: data === undefined ? undefined : JSON.stringify(data) }),
  delete: <T>(path: string, init: RequestInit = {}): Promise<T> =>
    request<T>(path, { ...init, method: 'DELETE' }),
};
